const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");
const trimLeadingSlash = (value) => String(value || "").replace(/^\/+/, "");
const normalizePath = (value) => {
  const clean = String(value || "").trim();
  if (!clean) return "";
  return `/${trimLeadingSlash(clean).replace(/\/+$/, "")}`;
};

const readEnv = (key, fallback = "") => {
  const value = process.env[key];
  return value === undefined || value === null || value === "" ? fallback : value;
};

// The IoT production UI is served over HTTPS. Browsers block direct HTTP AI
// requests from an HTTPS page as mixed content. Keep HTTP available for local
// development, but automatically use HTTPS for AI endpoints on secure pages.
const secureAiUrlForCurrentPage = (value) => {
  const clean = String(value || "").trim();
  if (!clean) return "";

  const pageUsesHttps =
    typeof window !== "undefined" &&
    window.location &&
    window.location.protocol === "https:";

  return pageUsesHttps && /^http:\/\//i.test(clean)
    ? clean.replace(/^http:\/\//i, "https://")
    : clean;
};

const readAiUrl = (key, fallback = "") =>
  trimTrailingSlash(secureAiUrlForCurrentPage(readEnv(key, fallback)));

const toWebSocketUrl = (value) => {
  const clean = String(value || "").trim();
  if (!clean) return "";
  if (/^wss?:\/\//i.test(clean)) return trimTrailingSlash(clean);
  if (/^https:\/\//i.test(clean)) return trimTrailingSlash(clean.replace(/^https:/i, "wss:"));
  if (/^http:\/\//i.test(clean)) return trimTrailingSlash(clean.replace(/^http:/i, "ws:"));
  return trimTrailingSlash(clean);
};

export const env = {
  appName: readEnv("REACT_APP_APP_NAME", "Sama.IOT"),
  appVersion: readEnv("REACT_APP_APP_VERSION", "2.0"),
  baseDomain: trimTrailingSlash(readEnv("REACT_APP_BASE_DOMAIN", "http://localhost")),
  baseDomainProd: trimTrailingSlash(
    readEnv("REACT_APP_BASE_DOMAIN_PROD", "https://sama-tech.ae")
  ),
  apiPort: readEnv("REACT_APP_API_PORT", ""),
  basePath: normalizePath(readEnv("REACT_APP_BASE_PATH", "/iot")),
  stripePublicKey: readEnv("REACT_APP_STRIPE_PUBLIC_KEY", ""),
  sensorsBaseUrl: trimTrailingSlash(readEnv("REACT_APP_SENSORS_BASE_URL", "")),
  otaBaseUrl: trimTrailingSlash(readEnv("REACT_APP_OTA_BASE_URL", "")),
  aiVoiceBaseUrl: readAiUrl("REACT_APP_AI_VOICE_BASE_URL", ""),
  bucketDevices: readEnv("REACT_APP_BUCKET_DEVICES", "Sensors"),
  defaultIcon: readEnv("REACT_APP_DEFAULT_ICON", "default-image.svg"),
  defaultDatastreamIcon: readEnv("REACT_APP_DEFAULT_DATASTREAM_ICON", "datastream.svg"),
  defaultShipmentIcon: readEnv("REACT_APP_DEFAULT_SHIPMENT_ICON", "shipping.svg"),
  defaultDeviceCodeIcon: readEnv("REACT_APP_DEFAULT_DEVICE_CODE_ICON", "device-code.png"),
  firebaseVapidKey: readEnv("REACT_APP_FIREBASE_VAPID_KEY", ""),
};

const buildBaseUrl = () => {
  const portSuffix = env.apiPort ? `:${env.apiPort}` : "";
  return `${env.baseDomain}${portSuffix}${env.basePath}`;
};

export const APP_NAME = env.appName;
export const APP_VERSION = env.appVersion;
export const BASE_DOMAIN = env.baseDomain;
export const BASE_DOMAIN_PROD = env.baseDomainProd;
export const STRIPE_PUBLIC_KEY = env.stripePublicKey;
export const BASE_URL = trimTrailingSlash(readEnv("REACT_APP_BASE_URL", buildBaseUrl()));
export const BASE_URL_API = trimTrailingSlash(
  readEnv("REACT_APP_BASE_URL_API", `${BASE_URL}/api-v1`)
);
const configuredAiVoiceProcessUrl = readAiUrl(
  "REACT_APP_AI_VOICE_PROCESS_URL",
  ""
);
const inferredAiApiBaseUrl = /\/voice\/process$/i.test(configuredAiVoiceProcessUrl)
  ? configuredAiVoiceProcessUrl.replace(/\/voice\/process$/i, "")
  : "";
const defaultAiApiBaseUrl = env.aiVoiceBaseUrl
  ? /\/pyv1$/i.test(env.aiVoiceBaseUrl)
    ? env.aiVoiceBaseUrl
    : `${env.aiVoiceBaseUrl}/pyv1`
  : inferredAiApiBaseUrl;

const aiRoute = (path) =>
  defaultAiApiBaseUrl ? `${defaultAiApiBaseUrl}/${String(path || "").replace(/^\/+/, "")}` : "";

export const AI_VOICE_PROCESS_URL = configuredAiVoiceProcessUrl || aiRoute("voice/process");
export const AI_VOICE_SESSION_RESET_URL = readAiUrl(
  "REACT_APP_AI_VOICE_SESSION_RESET_URL",
  aiRoute("voice/session/reset")
);
export const AI_VOICE_SESSION_CONTEXT_URL = readAiUrl(
  "REACT_APP_AI_VOICE_SESSION_CONTEXT_URL",
  aiRoute("voice/session/context")
);
export const AI_RAG_CHAT_URL = readAiUrl(
  "REACT_APP_AI_RAG_CHAT_URL",
  aiRoute("rag/chat")
);
export const AI_RAG_CHAT_VOICE_URL = readAiUrl(
  "REACT_APP_AI_RAG_CHAT_VOICE_URL",
  aiRoute("rag/chat/voice")
);
export const AI_RAG_CHAT_VOICE_STREAM_URL = readAiUrl(
  "REACT_APP_AI_RAG_CHAT_VOICE_STREAM_URL",
  aiRoute("rag/chat/voice/stream")
);
export const AI_RAG_CHAT_VOICE_LIVE_URL = toWebSocketUrl(
  secureAiUrlForCurrentPage(
    readEnv("REACT_APP_AI_RAG_CHAT_VOICE_LIVE_URL", aiRoute("rag/chat/voice/live"))
  )
);
export const AI_RAG_CHAT_HISTORY_URL = readAiUrl(
  "REACT_APP_AI_RAG_CHAT_HISTORY_URL",
  aiRoute("rag/chat/history")
);
export const AI_RAG_CHAT_NEW_URL = readAiUrl(
  "REACT_APP_AI_RAG_CHAT_NEW_URL",
  aiRoute("rag/chat/new")
);

export const STATIC_FILES = `${BASE_URL}/static/files/`;
export const STATIC_ICONS = `${BASE_URL}/static/files/icons/`;
export const SENSORS_URL =
  env.sensorsBaseUrl || `${env.baseDomainProd}/center`;
export const OTA_URL = env.otaBaseUrl || `${env.baseDomain}/test-ota`;
export const BUCKET_DEVICES = env.bucketDevices;
export const defaultIcon = env.defaultIcon;
export const defaultDatastreamIcon = env.defaultDatastreamIcon;
export const defaultShipmentIcon = env.defaultShipmentIcon;
export const defaultDeviceCodeIcon = env.defaultDeviceCodeIcon;
